// The island map as a screen of the game (PRD section 19.3): the live 3D island of H0 on its tilted
// camera, its floating labels, and in H2 the way into a tile: touching a tile with an interior opens
// its panel (19.5) with «Entrar», and pinching in on it past the closest view enters it (19.4).
import { Vector3 } from 'three';
import type { Game } from '../game';
import type { ScreenPoint } from '../input/gestures';
import { CameraRig, mapFraming, type CameraMode } from '../render/camera-rig';
import { IslandScene } from '../render/island-scene';
import { h, icon } from '../ui/dom';
import { ICONS } from '../ui/icons';
import { LabelLayer } from '../ui/labels';
import { squircle } from '../ui/controls';
import { TilePanel } from '../ui/tile-panel';
import { cellKey, ownedCells, TERRAIN, terrainAt, worldX, worldZ, type Island } from '../world/island';

export interface MapViewHandlers {
  readonly onEnter: (tile: string) => void;
  readonly invalidate: () => void;
}

/** A pinch that keeps going this far past the closest view, over an enterable tile, enters it. */
const ENTER_PINCH = 1.4;

export class MapView {
  readonly world: IslandScene;
  readonly rig: CameraRig;
  readonly labels: LabelLayer;
  readonly panel: TilePanel;
  private readonly island: Island;
  private readonly game: Game;
  private readonly handlers: MapViewHandlers;
  private readonly owned: ReturnType<typeof ownedCells>;
  private readonly marker: HTMLElement;
  private markerAnchor: Vector3 | undefined;
  private width = 1;
  private height = 1;
  private overflow = 1;
  private pinchAt: ScreenPoint | undefined;

  constructor(island: Island, labelRoot: HTMLElement, game: Game, opts: { readonly stress: boolean; readonly shadows: boolean; readonly camera: CameraMode }, handlers: MapViewHandlers) {
    this.island = island;
    this.game = game;
    this.handlers = handlers;
    this.owned = ownedCells(island);
    this.world = new IslandScene(island, { stress: opts.stress, shadows: opts.shadows });
    this.rig = new CameraRig(mapFraming(this.world.bounds));
    this.rig.setMode(opts.camera);
    this.labels = new LabelLayer(labelRoot, this.world.labels);
    this.marker = h('div', { class: 'select-marker select-marker--map', 'aria-hidden': 'true', hidden: true }, squircle(icon(ICONS.caretDown, 'icon select-marker__icon'), { size: 44, tone: 'gold' }));
    labelRoot.append(this.marker);
    this.panel = new TilePanel(game.data, {
      onEnter: () => {
        const tile = this.panel.shown;
        if (tile) handlers.onEnter(tile);
      },
      onClose: () => {
        this.closePanel();
        handlers.invalidate();
      },
    });
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.rig.setViewport({ width, height, obstacles: [] });
  }

  /** The tile with an interior at a screen point, if any: the prototype's tiles are linked to the map by id. */
  tileAt(p: ScreenPoint): string | undefined {
    const ground = this.rig.pick(p, this.width, this.height);
    if (!ground) return undefined;
    const col = Math.floor(ground.x + this.island.cols / 2);
    const row = Math.floor(ground.z + this.island.rows / 2);
    const tile = this.owned.get(cellKey(col, row));
    return tile && this.game.sim.world.tiles.get(tile.id)?.interior ? tile.id : undefined;
  }

  /** World centre of a map tile, on its ground. */
  tileCentre(id: string): Vector3 | undefined {
    const tile = this.island.tiles.find((t) => t.id === id);
    if (!tile || tile.cells.length === 0) return undefined;
    const xs = tile.cells.map(([c]) => worldX(this.island, c));
    const zs = tile.cells.map(([, r]) => worldZ(this.island, r));
    const [c0, r0] = tile.cells[0] ?? [0, 0];
    const top = TERRAIN[terrainAt(this.island, c0, r0) ?? 'p'].top;
    return new Vector3(xs.reduce((a, b) => a + b, 0) / xs.length, top, zs.reduce((a, b) => a + b, 0) / zs.length);
  }

  /** A touch on the map: opens the panel of a tile with an interior, or closes the panel. */
  tap(p: ScreenPoint): void {
    const id = this.tileAt(p);
    const tile = id && this.game.report().tiles.find((t) => t.id === id);
    if (tile) {
      this.panel.show(tile);
      const centre = this.tileCentre(tile.id);
      this.markerAnchor = centre?.clone().setY(centre.y + 1.7);
      this.marker.hidden = !this.markerAnchor;
      if (centre) this.keepInView(centre);
      // Placed now, not on the next frame: a slow first frame must not show it in the corner.
      this.placeMarker();
    } else {
      this.closePanel();
    }
    this.handlers.invalidate();
  }

  /** Slides the map so a tile stays in sight beside the side panel, or above the bottom sheet in portrait. */
  private keepInView(centre: Vector3): void {
    const p = this.rig.project(centre.x, centre.y, centre.z, this.width, this.height);
    const portrait = this.height > this.width;
    const target = portrait ? { x: this.width / 2, y: this.height * 0.3 } : { x: Math.min(p.x, (this.width - 380) * 0.55), y: Math.min(Math.max(p.y, this.height * 0.3), this.height * 0.7) };
    const hidden = portrait ? p.y > this.height * 0.42 : p.x > this.width - 420;
    if (hidden) this.rig.slideTo(p, target, this.width, this.height, performance.now());
  }

  closePanel(): void {
    this.panel.hide();
    this.markerAnchor = undefined;
    this.marker.hidden = true;
  }

  beginGesture(): void {
    this.overflow = 1;
    this.pinchAt = undefined;
    this.rig.beginGesture();
  }

  pan(from: ScreenPoint, to: ScreenPoint, now: number): void {
    this.rig.pan(from, to, this.width, this.height, now);
  }

  zoom(factor: number, at: ScreenPoint): void {
    this.overflow *= this.rig.zoom(factor, at, this.width, this.height);
    this.pinchAt = at;
  }

  /** Ends a camera gesture; a pinch that kept going past the closest view over a tile enters it. */
  endGesture(now: number): void {
    this.rig.endGesture(now);
    const at = this.pinchAt;
    const tile = at && this.overflow > ENTER_PINCH ? this.tileAt(at) : undefined;
    this.overflow = 1;
    if (tile) this.handlers.onEnter(tile);
  }

  /** Called on every drawn frame of the map. */
  frame(): void {
    this.labels.update(this.rig.camera, this.width, this.height);
    this.placeMarker();
  }

  private placeMarker(): void {
    if (!this.markerAnchor) return;
    const p = this.rig.project(this.markerAnchor.x, this.markerAnchor.y, this.markerAnchor.z, this.width, this.height);
    this.marker.style.transform = `translate3d(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px, 0) translate(-50%, -100%)`;
  }

  /** Refreshes the open panel's figures: plain DOM, so it never needs a frame of the scene. */
  refreshPanel(): void {
    const shown = this.panel.shown;
    const tile = shown && this.game.report().tiles.find((t) => t.id === shown);
    if (tile) this.panel.update(tile);
  }

  /** Screen point of a map tile's centre, for tests. */
  tileScreen(id: string): ScreenPoint | undefined {
    const c = this.tileCentre(id);
    return c ? this.rig.project(c.x, c.y, c.z, this.width, this.height) : undefined;
  }
}
